import { v4 as uuid } from 'uuid';
import {
  CustomFieldDefinition,
  CustomValues,
  FieldDataType,
} from '@alucio/aws-beacon-amplify/src/models';
import type { ObjectRecord } from '@alucio/aws-beacon-amplify/src/models';
import { OBJECT_RECORD_STATUS } from '@alucio/aws-beacon-amplify/src/API';
import { CustomFieldValuesMap } from 'src/types/orms';

export type ObjectWithId = {
  id: string,
  externalId?: string,
  syncStatus?: ObjectRecord['syncStatus'],
  status: OBJECT_RECORD_STATUS | ObjectRecord['status'],
  [key: string]: string | string[] | undefined,
}

export type FormValuesType = Record<string, string | string[] | ObjectWithId[]>;

// Object selectors use the first child field for the selected option ID.
export function getObjectSelectValues(field: CustomFieldDefinition, records: ObjectWithId[]): string[] {
  const selectionFieldId = field.objectSetting?.childrenFieldIds[0];
  if (!selectionFieldId) return [];
  return [...new Set(records
    .filter(record => record.status !== OBJECT_RECORD_STATUS.REMOVED)
    .flatMap(record => record[selectionFieldId] || []))];
}

export function getObjectSelectRecords(
  field: CustomFieldDefinition,
  childFields: CustomFieldDefinition[],
  records: ObjectWithId[],
  selectedIds: string[],
): ObjectWithId[] {
  const selectionFieldId = field.objectSetting?.childrenFieldIds[0];
  if (!selectionFieldId) return records;
  const activeRecords = records.filter(record => record.status !== OBJECT_RECORD_STATUS.REMOVED);
  const lockedIds = field.objectSetting?.restriction?.syncDelete
    ? getObjectSelectValues(field, activeRecords.filter(record => !!record.externalId)) : [];
  const defaultValues = getDefaultCustomFieldValuesMap(childFields);
  const defaults = Object.fromEntries(childFields.map(child => [
    child.id,
    child.fieldType === FieldDataType.MULTICATEGORICAL
      ? defaultValues[child.id]?.values || [] : defaultValues[child.id]?.values[0] ?? '',
  ]));

  return [...new Set([...selectedIds, ...lockedIds])].flatMap(selectedId => {
    const existing = activeRecords.filter(record => {
      const value = record[selectionFieldId];
      return Array.isArray(value) ? value.includes(selectedId) : value === selectedId;
    });
    return existing.length ? existing : [{
      ...defaults,
      id: uuid(),
      externalId: '',
      status: OBJECT_RECORD_STATUS.ACTIVE,
      [selectionFieldId]: selectedId,
    }];
  });
}

export const isArrayOfObjects = (value: unknown): boolean => {
  return Array.isArray(value) && value.every((item) => typeof item === 'object');
};

export const getDefaultCustomFieldValuesMap = (
  customFields: CustomFieldDefinition[],
): CustomFieldValuesMap => customFields.reduce((acc, customField) => {
  const defaultValues = customField.fieldValueDefinitions?.filter((fieldValueDefinition) =>
    fieldValueDefinition.isDefault && !fieldValueDefinition.disabled && fieldValueDefinition.value);

  if (defaultValues && defaultValues.length > 0) {
    const shouldUseIdAsTheValue =
      FieldDataType.MULTICATEGORICAL === customField.fieldType ||
      FieldDataType.CATEGORICAL === customField.fieldType;

    acc[customField.id] = {
      field: customField,
      values: defaultValues.map((defaultValue) => shouldUseIdAsTheValue ? defaultValue.id : defaultValue.value),
      displayValues: defaultValues.map((defaultValue) => defaultValue.label || defaultValue.value),
    };
  }
  return acc;
}, {});

export const getConvertedModelValuesToForm = (customValues: CustomValues[]): FormValuesType => {
  return customValues.reduce<FormValuesType>((acc, value) => {
    if (value.objectRecords) {
      acc[value.fieldId] = value.objectRecords.map<ObjectWithId>((objectRecord) => {
        const objectCustomValues = objectRecord.values.reduce((innerAcc, objectValue) => ({
          ...innerAcc,
          [objectValue.fieldId]: objectValue.values,
        }), {});

        return {
          ...objectCustomValues,
          id: objectRecord.id,
          externalId: objectRecord.externalId,
          status: objectRecord.status,
          syncStatus: objectRecord.syncStatus,
        };
      });
    } else {
      acc[value.fieldId] = value.values;
    }

    return acc;
  }, {});
};
